// Pure date math for the DatePicker primitive (ORP-18573). No Vue, no store, no Intl — the
// "Why nothing survives" mandate moves this out of the 807-ln god-component. Cross-module range
// arithmetic reuses `shared/lib/date` (`getCompareDateRange`); the rest is local calendar plumbing.

import type { CompareModeId, DatePresetId, MonthLabels } from './types'

import { getCompareDateRange } from '@/shared/lib/date/range'

/** Midnight-normalised clone (null-safe). */
export function startOfDay(date: Date | null | undefined): Date | null {
  if (!date) return null
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  return next
}

/** `date + days` as a new `Date`. */
export function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

/** Same calendar day? */
export function isSameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/** Is `date` inside the inclusive `[start, end]` span (day granularity)? */
export function isInRange(date: Date | null, start: Date | null, end: Date | null): boolean {
  if (!date || !start || !end) return false
  const time = startOfDay(date)!.getTime()
  return time >= startOfDay(start)!.getTime() && time <= startOfDay(end)!.getTime()
}

export interface ResolvedRange {
  start: Date
  end: Date
}

/**
 * The `[start, end]` a preset resolves to (parity: `OrphexDatePicker.vue:433–496`). `Maximum` clamps
 * to `min` (`firstAvailableDate`), else a one-year fallback (`:493`). `Custom` has no fixed range and
 * returns `null`. Returns `null` for any unknown id.
 */
export function resolvePreset(
  id: DatePresetId,
  today: Date,
  min: Date | null,
): ResolvedRange | null {
  const base = startOfDay(today)!
  const yesterday = addDays(base, -1)
  let start: Date | undefined
  let end: Date | undefined

  switch (id) {
    case 'Yesterday':
      start = yesterday
      end = yesterday
      break
    case 'This Week':
      start = addDays(yesterday, -(yesterday.getDay() === 0 ? 6 : yesterday.getDay() - 1))
      end = yesterday
      break
    case 'Last Week': {
      const dow = (base.getDay() + 6) % 7
      const thisMonday = addDays(base, -dow)
      start = addDays(thisMonday, -7)
      end = addDays(start, 6)
      break
    }
    case 'This Month':
      start = new Date(base.getFullYear(), base.getMonth(), 1)
      end = yesterday
      break
    case 'Last Month':
      start = new Date(base.getFullYear(), base.getMonth() - 1, 1)
      end = new Date(base.getFullYear(), base.getMonth(), 0)
      break
    case 'This Year':
      start = new Date(base.getFullYear(), 0, 1)
      end = yesterday
      break
    case 'Last Year':
      start = new Date(base.getFullYear() - 1, 0, 1)
      end = new Date(base.getFullYear() - 1, 11, 31)
      break
    case 'Last 7 Days':
      start = addDays(base, -7)
      end = yesterday
      break
    case 'Last 14 Days':
      start = addDays(base, -14)
      end = yesterday
      break
    case 'Last 28 Days':
      start = addDays(base, -28)
      end = yesterday
      break
    case 'Last 30 Days':
      start = addDays(base, -30)
      end = yesterday
      break
    case 'Last 60 Days':
      start = addDays(base, -60)
      end = yesterday
      break
    case 'Last 90 Days':
      start = addDays(base, -90)
      end = yesterday
      break
    case 'Maximum':
      start = min ?? addDays(base, -365)
      end = yesterday
      break
    case 'Custom':
      return null
  }

  if (start === undefined || end === undefined) return null
  // Parity `:497` — a preset whose start lands past yesterday collapses to a single day.
  if (start > yesterday) end = start
  return { start: startOfDay(start)!, end: startOfDay(end)! }
}

/**
 * The resolved comparison window for a mode (parity: `OrphexDatePicker.vue:262–301`). `Previous
 * Period` reuses `shared/lib/date`'s `getCompareDateRange`. `Custom` echoes the caller-held custom
 * dates. Any disabled / incomplete range yields `{ start: null, end: null }`.
 */
export function resolveCompareRange(
  mode: CompareModeId,
  start: Date | null,
  end: Date | null,
  custom: { start: Date | null; end: Date | null },
  enabled: boolean,
): { start: Date | null; end: Date | null } {
  if (mode === 'Custom') return { start: custom.start, end: custom.end }
  if (!enabled || !start || !end) return { start: null, end: null }

  switch (mode) {
    case 'Previous Period': {
      const { compareStart, compareEnd } = getCompareDateRange(start, end)
      return { start: compareStart, end: compareEnd }
    }
    case 'Previous Week':
      return { start: addDays(start, -7), end: addDays(end, -7) }
    case 'Previous Month': {
      const s = new Date(start)
      s.setMonth(s.getMonth() - 1)
      const e = new Date(end)
      e.setMonth(e.getMonth() - 1)
      return { start: s, end: e }
    }
    case 'Previous Year': {
      const s = new Date(start)
      s.setFullYear(s.getFullYear() - 1)
      const e = new Date(end)
      e.setFullYear(e.getFullYear() - 1)
      return { start: s, end: e }
    }
  }
}

/** One rendered calendar slot: a month header, an empty pad, or a selectable day. */
export interface CalendarCell {
  /** Full-width month heading (its own grid row). */
  isFullHeader?: boolean
  /** Inline month heading (shares a partial week row). */
  isInlineHeader?: boolean
  /** Heading text (`"<Month> <year>"`), present on header cells. */
  text?: string
  /** `"<year>-<monthIndex>"` scroll anchor, present on header cells. */
  monthId?: string
  date?: Date
  dayOfMonth?: number
  isDisabled?: boolean
  isSelected?: boolean
  isCompareSelected?: boolean
  isStart?: boolean
  isEnd?: boolean
  isCompareStart?: boolean
  isCompareEnd?: boolean
}

export interface CalendarGridParams {
  min: Date | null
  max: Date
  start: Date | null
  end: Date | null
  compareStart: Date | null
  compareEnd: Date | null
  compareEnabled: boolean
  monthLabels: MonthLabels
}

/**
 * Build the flat, scrollable multi-month grid (parity: `CalendarView.vue:90–154`). Months run from
 * `min` (or three years back) through the month of `max`. A day is disabled when it falls before
 * `min` or after `max`. Weekweeks are Monday-first (padding uses the ISO `(getDay()+6)%7` shift).
 */
export function buildCalendarGrid(params: CalendarGridParams): CalendarCell[] {
  const { min, max, start, end, compareStart, compareEnd, compareEnabled, monthLabels } = params
  const items: CalendarCell[] = []

  const anchor = start ?? end ?? max
  const startYear = min ? min.getFullYear() : anchor.getFullYear() - 3
  const startMonth = min ? min.getMonth() : 0
  const cursor = new Date(startYear, startMonth, 1)
  const endOfLoop = new Date(max.getFullYear(), max.getMonth() + 1, 0)

  const minDay = startOfDay(min)
  const maxDay = startOfDay(max)!

  while (cursor <= endOfLoop) {
    if (cursor.getDate() === 1) {
      const paddingDays = cursor.getDay() === 0 ? 6 : cursor.getDay() - 1
      const monthId = `${cursor.getFullYear()}-${cursor.getMonth()}`
      const text = `${monthLabels[cursor.getMonth()] ?? ''} ${cursor.getFullYear()}`

      if (paddingDays <= 1) {
        items.push({ isFullHeader: true, text, monthId })
        for (let i = 0; i < paddingDays; i++) items.push({})
      } else {
        for (let i = 0; i < paddingDays; i++) {
          items.push(i === 0 ? { isInlineHeader: true, text, monthId } : {})
        }
      }
    }

    const date = new Date(cursor)
    const day = startOfDay(date)!
    const isDisabled = (minDay !== null && day < minDay) || day > maxDay

    items.push({
      date,
      dayOfMonth: date.getDate(),
      isDisabled,
      isSelected: isInRange(date, start, end),
      isCompareSelected: compareEnabled && isInRange(date, compareStart, compareEnd),
      isStart: isSameDay(date, start),
      isEnd: isSameDay(date, end),
      isCompareStart: compareEnabled && isSameDay(date, compareStart),
      isCompareEnd: compareEnabled && isSameDay(date, compareEnd),
    })

    cursor.setDate(cursor.getDate() + 1)
  }

  return items
}
